<template>
  <div style="height:100%">
    <div>
      <v-row no-gutters>
        <v-col cols="12" lg="auto">
          <v-row>
            <v-col cols="auto" class="dateSelector ml-4 pb-0">
              <v-menu
                v-model="datePickerMenuFrom"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="dateFormattedFrom"
                    :label="$t('From date')"
                    prepend-icon="event"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  :max="maxDate"
                  :min="minDate"
                  v-model="dateFrom"
                  no-title
                  @input="datePickerMenuFrom = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="auto" class="dateSelector pb-0">
              <v-menu
                v-model="datePickerMenuTo"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="dateFormattedTo"
                    :label="$t('To date')"
                    prepend-icon="event"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  :max="maxDate"
                  :min="minDate"
                  v-model="dateTo"
                  no-title
                  @input="datePickerMenuTo = false"
                ></v-date-picker>
              </v-menu>
            </v-col>

            <v-col cols="auto" style="width:270px" class="pb-0">
              <v-select
                :label="$t('County')"
                :disabled="!countyEnabled"
                v-model="county"
                :items="availableCounties"
                clearable
              ></v-select>
            </v-col>
            <v-col cols="auto" style="width:270px" class="pb-0">
              <v-autocomplete v-model="city" :items="availableCities" :label="$t('Settlement')" clearable></v-autocomplete>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" lg="auto">
          <v-row>
            <v-col cols="auto" class="ml-4">
              <v-checkbox :label="$t('By primary address')" v-model="byPrimaryAddress"></v-checkbox>
            </v-col>
            <v-col cols="auto">
              <v-checkbox :label="$t('New cases per day')" v-model="dailyNewCases"></v-checkbox>
            </v-col>
            <v-col cols="auto" class="d-flex align-center mb-3">
              <v-btn
                @click="refresh()"
                :loading="isLoading"
                :disabled="isLoading"
                >{{$t('Refresh')}}
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </div>
    <div id="plotly" v-resize:debounce.100="onResize"></div>
  </div>
</template>

<script>
import plotly from 'plotly.js/dist/plotly-basic.min'
import api from "../services/ApiService";
import { getDates } from "../helpers/dateFormat";
import moment from "moment-mini";
import { mapState } from "vuex";
import { plotlyRegisterConfig } from "../helpers/chart/plotly";
import { translateLabel } from "../helpers/dataTranslations";

export default {
  name: "Chart",

  data() {
    return {
      minDate: undefined,
      maxDate: undefined,
      datePickerMenuFrom: false,
      dateFrom: undefined,
      datePickerMenuTo: false,
      dateTo: undefined,
      county: undefined,
      countyEnabled: true,
      availableCounties: [],
      availableCities: [],
      city: undefined,
      cityEnabled: true,
      byPrimaryAddress: false,
      dailyNewCases: false,
      isLoading: false
    };
  },

  computed: {
    ...mapState({
      statuses: state => state.app.config.statuses
    }),
    dateFormattedFrom() {
      return moment(this.dateFrom).format("YYYY.MM.DD");
    },
    dateFormattedTo() {
      return moment(this.dateTo).format("YYYY.MM.DD");
    }
  },

  watch: {
    async county(selectedCounty) {
      const { data: cities } = await api.getChartCities(selectedCounty);
      this.availableCities = cities;
    },
    "$i18n.locale"(){
      this.refresh();
    }
  },

  async mounted() {
    plotly.register(plotlyRegisterConfig);

    const { minDate, maxDate } = getDates();

    this.dateFrom = minDate;
    this.dateTo = maxDate;
    this.minDate = minDate;
    this.maxDate = maxDate;

    const { data: counties } = await api.getChartCounties();
    this.availableCounties = counties;
    this.county = "";

    this.refresh();
  },

  methods: {
    async refresh() {
      this.isLoading = true;
      const formattedDates = [];

      // dates for y axes
      const dates = [];
      const currentDate = moment(this.dateFrom);
      const to = moment(this.dateTo);

      while (currentDate <= to) {
        dates.push(currentDate.toDate());
        formattedDates.push(currentDate.format("YYYY.MM.DD"));

        currentDate.add(1, "days");
      }

      const params = {
        dateFrom: moment(this.dateFrom).format("YYYY-MM-DD"),
        dateTo: moment(this.dateTo).format("YYYY-MM-DD"),
        county: this.county,
        city: this.city,
        byPrimaryAddress: this.byPrimaryAddress,
        dailyNewCases: this.dailyNewCases
      };

      const { data: result } = await api.getChartStats(params);

      const data = [];

      const statusesAndSummmary = [
        ...translateLabel(this.statuses).filter(a => a.showOnMap),
        { color: "purple", value: "osszesen", label: this.$t("all") }
      ];

      statusesAndSummmary.forEach(status => {
        const trace = {
          name: status.label,
          x: formattedDates,
          y: [],
          type: "scatter",
          line: {
            color: status.color
          }
        };

        dates.forEach(date => {
          const value = result.find(x => moment(x.date).toISOString() === date.toISOString());
          if (value) {
            trace.y.push(value[status.value] || 0);
          } else {
            trace.y.push(0);
          }
        });

        data.push(trace);
      });

      let title = this.dateFormattedFrom + " - " + this.dateFormattedTo;
      if (this.county) {
        title += ", " + this.county;
      }
      if (this.city) {
        title += ", " + this.city;
      }
      if (this.byPrimaryAddress) {
        title += ", " + this.$t("By primary address").toLowerCase();
      }
      if (this.dailyNewCases) {
        title += ", "+ this.$t("New cases per day").toLowerCase();
      }

      plotly.newPlot(
        "plotly",
        data,
        {
          autosize: true,
          title: {
            text: title,
            xref: "paper",
            x: 0.05
          },
          legend: { orientation: "h" },
          xaxis: {
            nticks: 30
          }
        },
        {
          locale: this.$i18n.locale,
          toImageButtonOptions: {
            format: "png",
            filename: this.$t("Epidemic curve").toLowerCase(),
            height: 768,
            width: 1024,
            scale: 1
          },
          displaylogo: false,
          showTips: false,
          displayModeBar: true
        }
      );
      this.isLoading = false;
    },

    onResize() {
      plotly.Plots.resize("plotly");
    }
  }
};
</script>

<style>
.js-plotly-plot .plotly .modebar {
  right: 20px;
}
#plotly {
  height: calc(100vh - 250px);
  overflow: hidden;
}

@media only screen and (max-width: 1264px) {
  #plotly {
    height: calc(100vh - 350px);
  }
}

.plotly {
  height: 100%;
}

.dateSelector {
  width: 170px;
}
</style>
